import { configureEcho } from "@laravel/echo-react";
import Pusher from "pusher-js";
import { useAuthStore } from "@/store/auth.store";

declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

window.Pusher = Pusher;
// Désactiver en prod — réactiver pour debug WebSocket
// Pusher.logToConsole = true;

/**
 * Configure Echo, avec un jeton relu **à chaque** demande d'autorisation de canal.
 *
 * `configureEcho` ne met à jour qu'un objet de configuration : l'instance Echo, elle, est créée à
 * la première utilisation puis mise en cache et **jamais reconstruite** (`if (instance) return
 * instance`). Rappeler `initEcho` avec un nouveau jeton n'avait donc aucun effet sur l'instance
 * vivante, qui continuait d'authentifier `/broadcasting/auth` avec l'en-tête figé à sa création.
 *
 * La conséquence est une panne **sélective**, et c'est ce qui la rend trompeuse : `fetchJson` lit le
 * jeton courant dans le magasin à chaque appel, donc toute l'API continue de répondre normalement
 * pendant que le seul `/broadcasting/auth` échoue. Pire, si le jeton figé appartient à un autre
 * utilisateur — bascule d'identité — le backend authentifie cet autre utilisateur puis refuse le
 * canal : un **403**, et non un 401, exactement la signature relevée sur l'élève 237 le 18/08.
 *
 * `pusher-js` expose `headersProvider`, une fonction appelée au moment de la requête — c'est le
 * mécanisme prévu pour ce cas. Mais il ne le lit **que** sous `channelAuthorization` : l'ancienne
 * clé `auth` passe par une passerelle de compatibilité (`buildChannelAuth`) qui ne recopie que
 * `auth.params` et `auth.headers` et **jette silencieusement** `auth.headersProvider`. Posé sous
 * `auth`, le rappel n'était donc jamais appelé et `/broadcasting/auth` partait sans en-tête
 * `Authorization` : 401 « Non authentifié » pour tous les rôles, y compris admin.
 *
 * On déclare donc directement `channelAuthorization`, qui remplace intégralement `auth` +
 * `authEndpoint` côté `pusher-js` (dès que la clé est présente, la branche héritée est ignorée).
 * On ne pose pas `headers` en plus : `setRequestHeader` concatène les valeurs de même nom, et les
 * deux en-têtes produiraient un « Bearer ancien, Bearer nouveau » invalide.
 *
 * Le magasin est la **seule** source du jeton. Un repli sur un jeton de démarrage subsistait ici, et
 * il rouvrait discrètement la porte que ce correctif venait de fermer : dès que le magasin était
 * momentanément vide, Echo repartait avec le jeton figé à la construction. C'était le seul chemin
 * par lequel ce client pouvait présenter un jeton différent de celui du reste de l'application — et
 * un candidat sérieux au 401 du 25/08, dont le backend a relevé qu'il portait le bon propriétaire
 * mais une expiration dépassée de seize heures.
 *
 * Sans jeton, on n'envoie pas d'en-tête du tout : un refus franc vaut mieux qu'un jeton mort, qui
 * fait porter le soupçon sur le cycle de vie côté serveur alors que la faute est ici.
 */
export function initEcho() {
  configureEcho({
    broadcaster: "reverb",
    key: import.meta.env.VITE_REVERB_APP_KEY,
    wsHost: import.meta.env.VITE_REVERB_HOST,
    wsPort: Number(import.meta.env.VITE_REVERB_PORT) || 8080,
    wssPort: Number(import.meta.env.VITE_REVERB_PORT) || 443,
    forceTLS: import.meta.env.VITE_REVERB_SCHEME === "https",
    enabledTransports: ["ws", "wss"],
    channelAuthorization: {
      endpoint: `${import.meta.env.VITE_API_URL}/broadcasting/auth`,
      transport: "ajax",
      headersProvider: () => {
        const token = useAuthStore.getState().token;
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
    },
  });
}
